<template>
  <div class="pp-page">
    <div class="pp-card">
      <div class="pp-icon">
        <i class="mdi mdi-hospital-building"></i>
      </div>
      <h1 class="pp-title">Patient Portal</h1>
      <p class="pp-subtitle">Sign in to access your medical records</p>

      <label class="pp-label">Phone or Username</label>
      <div class="pp-input-wrap">
        <i class="mdi mdi-account pp-input-icon"></i>
        <input v-model="username" class="pp-input" placeholder="" @keyup.enter="login" autofocus />
      </div>

      <label class="pp-label mt-3">Password</label>
      <div class="pp-input-wrap">
        <i class="mdi mdi-lock pp-input-icon"></i>
        <input v-model="password" :type="showPass?'text':'password'" class="pp-input pp-input-pass" @keyup.enter="login" />
        <button type="button" class="pp-eye" @click="showPass = !showPass" :title="showPass?'Hide':'Show'">
          <i class="mdi" :class="showPass ? 'mdi-eye-off' : 'mdi-eye'"></i>
        </button>
      </div>

      <button class="pp-btn pp-btn-primary" :disabled="!username || !password || loggingIn" @click="login">
        {{ loggingIn ? 'Signing in…' : 'Sign In' }}
      </button>

      <div v-if="error" class="pp-error">{{ error }}</div>

      <div class="pp-demo">
        <i class="mdi mdi-information-outline"></i>
        <div>
          <strong>Demo:</strong> any of
          <code>0722111001</code>, <code>0722111003</code>, <code>0722111013</code>,
          <code>0722111021</code>, <code>0722111027</code> · password <code>demo1234</code>
        </div>
      </div>

      <div class="pp-divider"></div>

      <div class="pp-footer">
        <a href="javascript:void(0)" @click="$router.push('/')" class="pp-link"><i class="mdi mdi-arrow-left"></i> Back to home</a>
        <span class="pp-sep">·</span>
        <a href="javascript:void(0)" @click="$router.push('/login')" class="pp-link pp-link-bold">Staff sign-in</a>
      </div>
    </div>
  </div>
</template>

<script>
import { execute2, hms } from "@/api";

export default {
  name: "PatientSelfServiceLogin",
  data() {
    return { username: "", password: "", showPass: false, loggingIn: false, error: "" };
  },
  methods: {
    async login() {
      this.error = ""; this.loggingIn = true;
      try {
        const r = await execute2({ function: 'patientPortalLogin', username: this.username, password: this.password }, hms);
        if (r.data && r.data.success) {
          localStorage.setItem('patientPortalToken', r.data.data.token);
          localStorage.setItem('patientPortalUser', JSON.stringify(r.data.data.patient));
          const mustChange = !!r.data.data.must_change_password;
          if (mustChange) {
            localStorage.setItem('patientPortalMustChangePassword', '1');
            this.$router.push('/portal/change-password');
          } else {
            localStorage.removeItem('patientPortalMustChangePassword');
            this.$router.push('/portal');
          }
        } else {
          this.error = r.data?.message || 'Invalid credentials.';
        }
      } catch (e) { console.error(e); this.error = 'Network error.'; }
      finally { this.loggingIn = false; }
    }
  }
};
</script>

<style scoped>
.pp-page { min-height: 100vh; background: #f5f6f8; display: flex; align-items: center; justify-content: center; padding: 30px 20px; }

.pp-card { background: #fff; border-radius: 14px; padding: 36px 36px 28px; max-width: 460px; width: 100%; box-shadow: 0 4px 14px rgba(15, 23, 42, .06); text-align: center; }

.pp-icon { width: 80px; height: 80px; margin: 0 auto 18px; background: linear-gradient(135deg, #2c5fd1 0%, #1e3a8a 100%); border-radius: 16px; display: flex; align-items: center; justify-content: center; box-shadow: 0 6px 18px rgba(30, 58, 138, 0.25); }
.pp-icon i { font-size: 44px; color: #fff; }

.pp-title { font-size: 28px; font-weight: 800; color: #0f172a; margin: 0 0 6px; }
.pp-subtitle { color: #64748b; font-size: 14px; margin-bottom: 26px; }

.pp-label { font-size: 13px; font-weight: 600; color: #334155; display: block; margin-bottom: 6px; text-align: left; }

.pp-input-wrap { position: relative; }
.pp-input { width: 100%; padding: 12px 14px 12px 42px; border: 1px solid #cbd5e1; background: #eef2ff; border-radius: 10px; font-size: 15px; color: #0f172a; transition: .15s; box-sizing: border-box; }
.pp-input:focus { outline: none; border-color: #2c5fd1; background: #fff; box-shadow: 0 0 0 3px rgba(44, 95, 209, 0.12); }
.pp-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #64748b; font-size: 18px; pointer-events: none; }
.pp-input-pass { padding-right: 42px; }
.pp-eye { position: absolute; right: 8px; top: 50%; transform: translateY(-50%); background: none; border: none; cursor: pointer; color: #64748b; padding: 4px 8px; }
.pp-eye i { font-size: 18px; }

.pp-btn { width: 100%; padding: 13px 16px; border-radius: 10px; border: none; font-weight: 700; font-size: 15px; cursor: pointer; margin-top: 22px; transition: .15s; }
.pp-btn-primary { background: #6c8eff; color: #fff; }
.pp-btn-primary:hover { background: #4f73e8; }
.pp-btn-primary:disabled { opacity: .55; cursor: not-allowed; }

.pp-error { background: #fee2e2; color: #991b1b; padding: 10px 12px; border-radius: 8px; margin-top: 14px; font-size: 13px; text-align: left; }

.pp-demo { margin-top: 18px; padding: 12px 14px; background: #eef2ff; border-radius: 8px; font-size: 13px; color: #475569; line-height: 1.6; display: flex; align-items: flex-start; gap: 8px; text-align: left; }
.pp-demo i { color: #6c8eff; font-size: 18px; flex-shrink: 0; margin-top: 1px; }
.pp-demo strong { color: #1e293b; }
.pp-demo code { background: #fff; padding: 1px 6px; border-radius: 4px; font-size: 12px; color: #2c5fd1; font-weight: 600; border: 1px solid #c7d2fe; }

.pp-divider { height: 1px; background: #e2e8f0; margin: 22px 0 16px; }

.pp-footer { display: flex; justify-content: center; align-items: center; gap: 10px; font-size: 14px; }
.pp-link { color: #2c5fd1; text-decoration: none; font-weight: 500; }
.pp-link:hover { text-decoration: underline; }
.pp-link-bold { font-weight: 700; }
.pp-sep { color: #cbd5e1; }
.mt-3 { margin-top: 14px !important; }
</style>
